import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { apiRequest, ApiError, NetworkUnavailableError, setTokenProvider, setTokenRefresher, setUnauthorizedHandler } from '../api/client'
import type { LoginResponse, User } from '../api/types'
import { clearUserPartition, resumeAuthRequiredOutbox } from '../offline/db'
import { activateSyncSession, AUTH_EXPIRED_EVENT, type SyncSession } from '../offline/sync'
import { disconnectPush, requestPushPermissionOnPwaEntry, restorePushSubscription } from '../notifications/push'
import { setRequestUserProvider } from './requestSession'

const STORAGE_KEY = 'amalthea.auth.v1'
const AUTH_CHANNEL = 'amalthea.auth.channel.v1'

interface StoredSession {
  token: string | null
  refreshToken?: string | null
  user: User
  activeRole?: string
  authExpired?: boolean
  version?: string
}

interface AuthContextValue {
  user: User | null
  token: string | null
  syncSession: SyncSession | null
  authExpired: boolean
  ready: boolean
  activeRole: string | null
  login(email: string, password: string): Promise<void>
  reauthenticate(email: string, password: string): Promise<void>
  logout(): Promise<void>
  switchRole(role: string): Promise<void>
  hasAnyRole(...roles: string[]): boolean
}

interface AuthChannelMessage {
  type: 'auth-changed'
  sourceId: string
}

const AuthContext = createContext<AuthContextValue | null>(null)

function parseStoredSession(raw: string | null): StoredSession | null {
  try {
    if (!raw) return null
    const value = JSON.parse(raw) as StoredSession
    if (!value?.user?.id) return null
    if (!value.authExpired && !value.token) return null
    return value
  } catch {
    return null
  }
}

function readStoredSession(): StoredSession | null {
  return parseStoredSession(localStorage.getItem(STORAGE_KEY))
}

function normalizeLogin(value: LoginResponse | { access_token?: string; refreshToken?: string }): LoginResponse {
  const token = 'token' in value ? value.token : value.access_token
  if (!token) throw new Error('Не удалось завершить вход. Повторите действие')
  return { token, refreshToken: 'refreshToken' in value ? value.refreshToken : undefined, user: 'user' in value ? value.user : undefined }
}

function createSyncSession(session: StoredSession): SyncSession | null {
  if (!session.token || session.authExpired) return null
  return Object.freeze({
    sessionId: session.version ?? crypto.randomUUID(),
    userId: session.user.id,
    token: session.token,
  })
}

/** Зеркало role_hierarchy backend (config/packages/security.yaml):
 *  ROLE_PLANNER — планирование и контроль работ, дефекты, журналы, черновик отчёта;
 *  ROLE_DATA_EDITOR — ППР, оборудование, компоненты, справочники, программы ТО;
 *  ROLE_APPROVER — проверка выполненных работ, закрытие дефектов, утверждение отчёта;
 *  ROLE_DEFECT_CLOSER — закрытие устранённых дефектов;
 *  ROLE_REWORKER — возврат выполненных работ исполнителю на доработку;
 *  ROLE_TEAM_MANAGER — учётные записи исполнителей: логин, пароль, доступ, объекты. */
const roleHierarchy: Record<string, string[]> = {
  ROLE_ADMIN: ['ROLE_ADMIN', 'ROLE_ENGINEER', 'ROLE_SUPERVISOR', 'ROLE_MANAGER', 'ROLE_PLANNER', 'ROLE_DATA_EDITOR', 'ROLE_APPROVER', 'ROLE_DEFECT_CLOSER', 'ROLE_REWORKER', 'ROLE_TEAM_MANAGER', 'ROLE_EXECUTOR'],
  ROLE_ENGINEER: ['ROLE_ENGINEER', 'ROLE_PLANNER', 'ROLE_DATA_EDITOR', 'ROLE_EXECUTOR'],
  ROLE_SUPERVISOR: ['ROLE_SUPERVISOR', 'ROLE_PLANNER', 'ROLE_APPROVER', 'ROLE_DEFECT_CLOSER', 'ROLE_TEAM_MANAGER', 'ROLE_EXECUTOR'],
  ROLE_MANAGER: ['ROLE_MANAGER', 'ROLE_PLANNER', 'ROLE_DEFECT_CLOSER', 'ROLE_TEAM_MANAGER', 'ROLE_REWORKER', 'ROLE_EXECUTOR'],
  ROLE_APPROVER: ['ROLE_APPROVER', 'ROLE_DEFECT_CLOSER'],
  ROLE_EXECUTOR: ['ROLE_EXECUTOR'],
}

function sessionRole(session: StoredSession | null): string | null {
  if (!session) return null
  if (session.activeRole && session.user.roles.includes(session.activeRole)) return session.activeRole
  return session.user.roles[0] ?? null
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [initial] = useState(() => readStoredSession())
  const [user, setUser] = useState<User | null>(initial?.user ?? null)
  const [token, setToken] = useState<string | null>(initial?.authExpired ? null : initial?.token ?? null)
  const [syncSession, setSyncSession] = useState<SyncSession | null>(() => initial ? createSyncSession(initial) : null)
  const [authExpired, setAuthExpired] = useState(Boolean(initial?.authExpired))
  const [activeRole, setActiveRole] = useState<string | null>(() => sessionRole(initial))
  const [ready, setReady] = useState(false)
  const tokenRef = useRef<string | null>(initial?.authExpired ? null : initial?.token ?? null)
  const userRef = useRef<User | null>(initial?.user ?? null)
  const activeRoleRef = useRef<string | null>(sessionRole(initial))
  const syncSessionRef = useRef<SyncSession | null>(syncSession)
  const channelRef = useRef<BroadcastChannel | null>(null)
  const sourceIdRef = useRef(crypto.randomUUID())
  const refreshPromiseRef = useRef<Promise<string | null> | null>(null)

  useLayoutEffect(() => {
    setTokenProvider(() => tokenRef.current)
    setRequestUserProvider(() => userRef.current?.id ?? null)
    activateSyncSession(syncSessionRef.current)
    return () => {
      setRequestUserProvider(() => null)
      activateSyncSession(null)
    }
  }, [])

  const setSession = useCallback(async (
    session: StoredSession | null,
    options: { persist?: boolean; broadcast?: boolean } = {},
  ) => {
    const { persist = true, broadcast = true } = options
    const nextSyncSession = session ? createSyncSession(session) : null
    syncSessionRef.current = nextSyncSession
    activateSyncSession(nextSyncSession)
    tokenRef.current = session?.authExpired ? null : session?.token ?? null
    userRef.current = session?.user ?? null
    activeRoleRef.current = sessionRole(session)
    setToken(tokenRef.current)
    setUser(userRef.current)
    setActiveRole(activeRoleRef.current)
    setSyncSession(nextSyncSession)
    setAuthExpired(Boolean(session?.authExpired))

    if (persist) {
      if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
      else localStorage.removeItem(STORAGE_KEY)
    }
    if (broadcast) channelRef.current?.postMessage({ type: 'auth-changed', sourceId: sourceIdRef.current } satisfies AuthChannelMessage)
  }, [])

  const applyExternalSession = useCallback(async (session: StoredSession | null) => {
    const previousUserId = userRef.current?.id
    await setSession(session, { persist: false, broadcast: false })
    if (!session && previousUserId) await clearUserPartition(previousUserId)
    window.dispatchEvent(new CustomEvent('amalthea:queue-changed'))
  }, [setSession])

  useEffect(() => {
    const storageChanged = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) void applyExternalSession(parseStoredSession(event.newValue))
    }
    window.addEventListener('storage', storageChanged)

    if (typeof BroadcastChannel === 'undefined') {
      return () => window.removeEventListener('storage', storageChanged)
    }

    const channel = new BroadcastChannel(AUTH_CHANNEL)
    channelRef.current = channel
    channel.onmessage = (event: MessageEvent<AuthChannelMessage>) => {
      if (event.data?.type === 'auth-changed' && event.data.sourceId !== sourceIdRef.current) {
        void applyExternalSession(readStoredSession())
      }
    }
    return () => {
      window.removeEventListener('storage', storageChanged)
      channelRef.current = null
      channel.close()
    }
  }, [applyExternalSession])

  const expireSession = useCallback(async (userId: string) => {
    const currentUser = userRef.current
    if (!currentUser || currentUser.id !== userId) return
    await setSession({
      token: null,
      user: currentUser,
      activeRole: activeRoleRef.current ?? undefined,
      authExpired: true,
      version: crypto.randomUUID(),
    })
  }, [setSession])

  useLayoutEffect(() => {
    setUnauthorizedHandler(({ token: rejectedToken }) => {
      const currentUser = userRef.current
      if (currentUser && tokenRef.current === rejectedToken) void expireSession(currentUser.id)
    })
    return () => setUnauthorizedHandler(null)
  }, [expireSession])

  useLayoutEffect(() => {
    setTokenRefresher(async ({ token: rejectedToken }) => {
      if (tokenRef.current !== rejectedToken) return tokenRef.current
      const current = readStoredSession()
      if (!current?.refreshToken || current.authExpired) return null
      if (!refreshPromiseRef.current) {
        refreshPromiseRef.current = (async () => {
          try {
            const response = normalizeLogin(await apiRequest<LoginResponse>('/auth/refresh', {
              method: 'POST', body: { refreshToken: current.refreshToken }, authToken: null, notifyOnUnauthorized: false,
            }))
            const currentUser = response.user ?? await apiRequest<User>('/auth/me', { authToken: response.token, notifyOnUnauthorized: false })
            if (currentUser.id !== current.user.id || !response.refreshToken) return null
            await setSession({ ...current, token: response.token, refreshToken: response.refreshToken, user: currentUser, authExpired: false, version: crypto.randomUUID() })
            return response.token
          } catch { return null }
          finally { refreshPromiseRef.current = null }
        })()
      }
      return refreshPromiseRef.current
    })
    return () => setTokenRefresher(null)
  }, [setSession])

  useEffect(() => {
    const expired = (event: Event) => {
      const userId = (event as CustomEvent<{ userId?: string }>).detail?.userId
      if (userId) void expireSession(userId)
    }
    window.addEventListener(AUTH_EXPIRED_EVENT, expired)
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, expired)
  }, [expireSession])

  useEffect(() => {
    let cancelled = false
    async function restore() {
      if (!initial || initial.authExpired) {
        setReady(true)
        return
      }
      try {
        if (navigator.onLine && initial.token) {
          // Сеть может «числиться» подключённой без реального доступа (авиарежим с Wi-Fi, портал авторизации):
          // сохранённая сессия открывается не позже чем через несколько секунд, проверка не блокирует запуск.
          const upgraded = initial.refreshToken
            ? null
            : await apiRequest<LoginResponse>('/auth/session', { method: 'POST', authToken: initial.token, timeout: 4_000, notifyOnUnauthorized: false })
          const activeToken = upgraded?.token ?? initial.token
          const current = upgraded?.user ?? await apiRequest<User>('/auth/me', { authToken: activeToken, timeout: 4_000 })
          if (!cancelled) {
            await setSession({ ...initial, token: activeToken, refreshToken: upgraded?.refreshToken ?? initial.refreshToken, user: current, authExpired: false })
            void requestPushPermissionOnPwaEntry().then(restorePushSubscription).catch(() => undefined)
          }
        }
      } catch (error) {
        if (error instanceof ApiError && error.status === 401 && !cancelled) await expireSession(initial.user.id)
        if (!(error instanceof ApiError) && !(error instanceof NetworkUnavailableError)) console.error(error)
      } finally {
        if (!cancelled) setReady(true)
      }
    }
    void restore()
    return () => {
      cancelled = true
    }
  }, [expireSession, initial, setSession])

  /** Вход по логину; сотрудник с рабочей почтой может ввести её вместо логина. */
  const login = useCallback(async (identifier: string, password: string) => {
    // Start the browser permission request from the sign-in gesture, before awaiting the network.
    const pwaPermission = requestPushPermissionOnPwaEntry()
    const response = normalizeLogin(await apiRequest<LoginResponse | { access_token?: string; refreshToken?: string }>('/auth/login', {
      method: 'POST',
      body: { login: identifier, password },
      authToken: null,
    }))
    const currentUser = response.user ?? (await apiRequest<User>('/auth/me', { authToken: response.token }))
    if (userRef.current?.id !== currentUser.id) await disconnectPush()
    await setSession({
      token: response.token,
      refreshToken: response.refreshToken ?? null,
      user: currentUser,
      activeRole: activeRoleRef.current ?? undefined,
      version: crypto.randomUUID(),
    })
    void pwaPermission.then(restorePushSubscription).catch(() => undefined)
  }, [setSession])

  const reauthenticate = useCallback(async (identifier: string, password: string) => {
    // Re-authentication is also initiated by a user gesture, so it can show the native prompt.
    const pwaPermission = requestPushPermissionOnPwaEntry()
    const expectedUser = userRef.current
    if (!expectedUser || !authExpired) throw new Error('Повторный вход сейчас не требуется')

    const response = normalizeLogin(await apiRequest<LoginResponse | { access_token?: string; refreshToken?: string }>('/auth/login', {
      method: 'POST',
      body: { login: identifier, password },
      authToken: null,
    }))
    const currentUser = response.user ?? (await apiRequest<User>('/auth/me', { authToken: response.token }))
    if (currentUser.id !== expectedUser.id) {
      throw new Error(`Войдите под учётной записью ${expectedUser.login || expectedUser.email}. Сохранённые действия принадлежат ей.`)
    }

    await resumeAuthRequiredOutbox(expectedUser.id)
    await setSession({
      token: response.token,
      refreshToken: response.refreshToken ?? null,
      user: currentUser,
      activeRole: activeRoleRef.current ?? undefined,
      version: crypto.randomUUID(),
    })
    void pwaPermission.then(restorePushSubscription).catch(() => undefined)
    window.dispatchEvent(new CustomEvent('amalthea:queue-changed'))
  }, [authExpired, setSession])

  const logout = useCallback(async () => {
    const currentUserId = userRef.current?.id
    const currentRefreshToken = readStoredSession()?.refreshToken
    if (currentRefreshToken && navigator.onLine) void apiRequest('/auth/logout', { method: 'POST', body: { refreshToken: currentRefreshToken }, authToken: null, notifyOnUnauthorized: false }).catch(() => undefined)
    await disconnectPush()
    await setSession(null)
    if (currentUserId) await clearUserPartition(currentUserId)
  }, [setSession])

  const switchRole = useCallback(async (role: string) => {
    const currentUser = userRef.current
    if (!currentUser || !currentUser.roles.includes(role)) throw new Error('Эта роль не назначена вашей учётной записи')
    await setSession({
      token: tokenRef.current,
      refreshToken: readStoredSession()?.refreshToken ?? null,
      user: currentUser,
      activeRole: role,
      authExpired,
      version: syncSessionRef.current?.sessionId ?? crypto.randomUUID(),
    })
  }, [authExpired, setSession])

  const hasAnyRole = useCallback((...roles: string[]) => {
    const role = activeRoleRef.current
    if (!role) return false
    const effective = roleHierarchy[role] ?? [role]
    return roles.some((expected) => effective.includes(expected))
  }, [])

  const value = useMemo(
    () => ({ user, token, syncSession, authExpired, ready, activeRole, login, reauthenticate, logout, switchRole, hasAnyRole }),
    [user, token, syncSession, authExpired, ready, activeRole, login, reauthenticate, logout, switchRole, hasAnyRole],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext)
  if (!value) throw new Error('AuthProvider is missing')
  return value
}
